import { createElement, useState } from "react";
import { FaEllipsisH } from "react-icons/fa";
import { FilterBarPill } from "./FilterBarPill";
import { Filter } from "./types/filter.types";

const MORE_KEY = "__more__";

/**
 * Renders table filters as a row of compact pills meant to sit inline in the
 * table toolbar. Filters flagged `barPrimary` get their own pill; the rest are
 * grouped under a single "More" pill. Each pill opens a dropdown panel holding
 * the existing filter control.
 */
export const TableFilterPills = <T,>({
  filters,
}: {
  filters: {
    [key: string]: Filter<T>;
  };
}) => {
  const [openKey, setOpenKey] = useState<string | null>(null);

  const toggle = (key: string) => {
    setOpenKey((current) => (current === key ? null : key));
  };
  const close = () => setOpenKey(null);

  const keys = Object.keys(filters);
  const primaryKeys = keys.filter((key) => filters[key].barPrimary);
  const moreKeys = keys.filter((key) => !filters[key].barPrimary);
  const moreActiveCount = moreKeys.filter((key) => filters[key].barActive).length;

  const renderControl = (key: string) => {
    const {
      component,
      barWidth,
      barPrimary,
      barActive,
      barValueLabel,
      barClear,
      barPillComponent,
      ...rest
    } = filters[key];

    return createElement(component, { ...rest });
  };

  const clearMore = () => {
    moreKeys.forEach((key) => filters[key].barClear?.());
  };

  return (
    <div className="flex items-center gap-2">
      {primaryKeys.map((key) => {
        const filter = filters[key];

        if (filter.barPillComponent) {
          return createElement(filter.barPillComponent, {
            key,
            isOpen: openKey === key,
            onToggle: () => toggle(key),
            onClose: close,
          });
        }

        return (
          <FilterBarPill
            key={key}
            icon={filter.icon}
            label={filter.title}
            valueLabel={filter.barValueLabel}
            isActive={filter.barActive}
            isOpen={openKey === key}
            onToggle={() => toggle(key)}
            onClose={close}
            onClear={filter.barClear}
            panelWidth={filter.barWidth}
          >
            {renderControl(key)}
          </FilterBarPill>
        );
      })}

      {moreKeys.length > 0 && (
        <FilterBarPill
          icon={FaEllipsisH}
          label="More"
          valueLabel={moreActiveCount > 0 ? `(${moreActiveCount})` : undefined}
          isActive={moreActiveCount > 0}
          isOpen={openKey === MORE_KEY}
          onToggle={() => toggle(MORE_KEY)}
          onClose={close}
          onClear={clearMore}
          panelWidth={340}
        >
          <div className="overflow-y-auto" style={{ maxHeight: "60vh" }}>
            {moreKeys.map((key) => (
              <div key={key}>{renderControl(key)}</div>
            ))}
          </div>
        </FilterBarPill>
      )}
    </div>
  );
};
